import React, { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import { useAuth } from "../../../context/AuthContext";

export default function CreateStaffForm() {
  const { user } = useAuth();

  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
  });

  const [banner, setBanner] = useState({
    type: "",
    message: "",
  });

  const [createdData, setCreatedData] = useState(null);
  const [loading, setLoading] = useState(false);

  const canCreate = useMemo(() => user?.role === "admin", [user]);

  const handleChange = (field) => (event) => {
    setForm((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!canCreate) return;

    const token = localStorage.getItem("accessToken");

    if (!token) {
      setBanner({
        type: "error",
        message: "No se encontró la sesión administrativa",
      });
      return;
    }

    setLoading(true);
    setBanner({ type: "", message: "" });

    try {
      const response = await fetch("/api/gym/admin/users/staff/create/", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(form),
      });

      const data = await response.json();

      if (!response.ok) {
        setBanner({
          type: "error",
          message: data.error || "No se pudo crear el usuario staff",
        });
        return;
      }

      setBanner({
        type: "success",
        message: data.message || "Staff creado correctamente",
      });

      setCreatedData(data);

      setForm({
        first_name: "",
        last_name: "",
        email: "",
        phone: "",
      });
    } catch {
      setBanner({
        type: "error",
        message: "Ocurrió un error al conectar con el servidor",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async () => {
    if (!createdData?.share_message) return;

    try {
      await navigator.clipboard.writeText(createdData.share_message);
      setBanner({
        type: "success",
        message: "Mensaje copiado correctamente",
      });
    } catch {
      setBanner({
        type: "error",
        message: "No se pudo copiar el mensaje",
      });
    }
  };

  const handleWhatsApp = () => {
    if (!createdData?.share_message) return;
    const text = encodeURIComponent(createdData.share_message);
    window.open(`https://wa.me/?text=${text}`, "_blank");
  };

  if (!canCreate) {
    return null;
  }

  return (
    <Paper
      sx={{
        width: "100%",
        borderRadius: "18px",
        border: "1px solid rgba(148,163,184,0.2)",
        boxShadow: "0 16px 36px rgba(15,23,42,0.05)",
        p: { xs: 2, md: 2.4 },
        bgcolor: "#ffffff",
      }}
    >
      <Typography
        sx={{
          color: "#475569",
          fontSize: 13.2,
          fontWeight: 500,
          lineHeight: 1.5,
          mb: 2,
        }}
      >
        Se generará una clave temporal de 5 dígitos automáticamente.
      </Typography>

      {banner.message && (
        <Alert
          severity={banner.type}
          sx={{
            borderRadius: "12px",
            mb: 1.8,
          }}
        >
          {banner.message}
        </Alert>
      )}

      <Box component="form" onSubmit={handleSubmit} sx={{ width: "100%" }}>
        <Stack spacing={2}>
          <Box
            sx={{
              width: "100%",
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
              gap: 2,
            }}
          >
            <TextField
              label="Nombres"
              value={form.first_name}
              onChange={handleChange("first_name")}
              fullWidth
              disabled={loading}
              size="small"
              sx={{
                "& .MuiOutlinedInput-root": {
                  height: 40,
                  borderRadius: "10px",
                  bgcolor: "#f8fafc",
                },
                "& .MuiInputLabel-root": {
                  fontWeight: 500,
                  color: "#64748b",
                },
              }}
            />

            <TextField
              label="Apellidos"
              value={form.last_name}
              onChange={handleChange("last_name")}
              fullWidth
              disabled={loading}
              size="small"
              sx={{
                "& .MuiOutlinedInput-root": {
                  height: 40,
                  borderRadius: "10px",
                  bgcolor: "#f8fafc",
                },
                "& .MuiInputLabel-root": {
                  fontWeight: 500,
                  color: "#64748b",
                },
              }}
            />
          </Box>

          <Box
            sx={{
              width: "100%",
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
              gap: 2,
            }}
          >
            <TextField
              label="Correo"
              value={form.email}
              onChange={handleChange("email")}
              fullWidth
              disabled={loading}
              size="small"
              sx={{
                "& .MuiOutlinedInput-root": {
                  height: 40,
                  borderRadius: "10px",
                  bgcolor: "#f8fafc",
                },
                "& .MuiInputLabel-root": {
                  fontWeight: 500,
                  color: "#64748b",
                },
              }}
            />

            <TextField
              label="Teléfono"
              value={form.phone}
              onChange={handleChange("phone")}
              fullWidth
              disabled={loading}
              size="small"
              sx={{
                "& .MuiOutlinedInput-root": {
                  height: 40,
                  borderRadius: "10px",
                  bgcolor: "#f8fafc",
                },
                "& .MuiInputLabel-root": {
                  fontWeight: 500,
                  color: "#64748b",
                },
              }}
            />
          </Box>

          <Box>
            <Button
              type="submit"
              variant="contained"
              disabled={loading}
              sx={{
                height: 36,
                minWidth: 120,
                borderRadius: "10px",
                px: 1.8,
                fontWeight: 600,
                fontSize: 13,
                textTransform: "none",
                bgcolor: "#334155",
                boxShadow: "none",
                "&:hover": {
                  bgcolor: "#1e293b",
                  boxShadow: "none",
                },
              }}
            >
              {loading ? "Creando..." : "Crear staff"}
            </Button>
          </Box>
        </Stack>
      </Box>

      {createdData?.temporary_password && (
        <Paper
          sx={{
            mt: 1.8,
            borderRadius: "14px",
            p: 1.8,
            bgcolor: "#f8fafc",
            border: "1px solid rgba(148,163,184,0.18)",
            boxShadow: "none",
          }}
        >
          <Typography
            sx={{
              color: "#0f172a",
              fontSize: 15,
              fontWeight: 600,
              lineHeight: 1.2,
              mb: 0.8,
            }}
          >
            Acceso generado
          </Typography>

          <Typography
            sx={{
              color: "#475569",
              fontSize: 13.3,
              fontWeight: 500,
              lineHeight: 1.5,
              mb: 0.4,
            }}
          >
            Clave temporal: {createdData.temporary_password}
          </Typography>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ mt: 1.5 }}>
            <Button
              onClick={handleCopy}
              variant="outlined"
              startIcon={<ContentCopyRoundedIcon />}
              sx={{
                height: 36,
                borderRadius: "10px",
                px: 1.4,
                textTransform: "none",
                fontWeight: 600,
                fontSize: 12.8,
                color: "#334155",
                borderColor: "rgba(51,65,85,0.18)",
                "&:hover": {
                  borderColor: "rgba(51,65,85,0.28)",
                  bgcolor: "rgba(148,163,184,0.08)",
                },
              }}
            >
              Copiar
            </Button>

            <Button
              onClick={handleWhatsApp}
              variant="contained"
              startIcon={<WhatsAppIcon />}
              sx={{
                height: 36,
                borderRadius: "10px",
                px: 1.4,
                textTransform: "none",
                fontWeight: 600,
                fontSize: 12.8,
                bgcolor: "#25D366",
                boxShadow: "none",
                "&:hover": {
                  bgcolor: "#1ebe5d",
                  boxShadow: "none",
                },
              }}
            >
              Compartir por WhatsApp
            </Button>
          </Stack>
        </Paper>
      )}
    </Paper>
  );
}